// Single source of truth for the CRM account number series. Adding a new
// series (like 9 = TTC) means adding one entry here -- every filter, badge,
// picker and "billed account" business rule reads from this list.
//
// The database side is already series-agnostic: account_series is just the
// first character of the account number in the export views and RPCs.
// supabase/functions/driver-validation-matching keeps its own copy of the
// billed-series prefixes (edge functions can't import from src/) -- keep it
// in sync with ACCOUNT_SERIES_KEYS below.

export interface AccountSeries {
  key: string;
  /** Short display name, e.g. "ACC" */
  label: string;
  /** Value stored in crm_accounts.account_series_type */
  type: string;
  description: string;
  /** Tailwind classes for the small coloured series badge */
  badgeClass: string;
}

export const ACCOUNT_SERIES: AccountSeries[] = [
  { key: '5', label: 'ACC', type: 'ACC', description: 'ACC accounts starting with 5', badgeClass: 'bg-blue-900/40 text-blue-400 border-blue-700/50' },
  { key: '6', label: 'Corporate', type: 'Corporate', description: 'Corporate accounts starting with 6', badgeClass: 'bg-emerald-900/40 text-emerald-400 border-emerald-700/50' },
  { key: '7', label: 'Resthome', type: 'Resthome', description: 'Resthome/Facility accounts starting with 7', badgeClass: 'bg-purple-900/40 text-purple-400 border-purple-700/50' },
  { key: '8', label: 'Personal', type: 'Personal', description: 'Personal accounts starting with 8', badgeClass: 'bg-orange-900/40 text-orange-400 border-orange-700/50' },
  { key: '9', label: 'TTC', type: 'TTC', description: 'TTC accounts starting with 9', badgeClass: 'bg-cyan-900/40 text-cyan-400 border-cyan-700/50' },
];

export const ACCOUNT_SERIES_KEYS: string[] = ACCOUNT_SERIES.map((s) => s.key);

export const ACCOUNT_SERIES_LABELS: Record<string, string> = Object.fromEntries(
  ACCOUNT_SERIES.map((s) => [s.key, s.label])
);

const FALLBACK_BADGE_CLASS = 'bg-gray-800/40 text-gray-400 border-gray-700/50';

export const seriesBadgeClass = (key: string | null | undefined): string =>
  ACCOUNT_SERIES.find((s) => s.key === key)?.badgeClass ?? FALLBACK_BADGE_CLASS;

/** True if an account id belongs to one of the billed CRM series (first digit). */
export const isBilledSeriesAccountId = (accountId: string | null | undefined): boolean => {
  const first = accountId?.toString().trim().charAt(0);
  return !!first && ACCOUNT_SERIES_KEYS.includes(first);
};
